import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Datagrid,
  DateField,
  FunctionField,
  ListContextProvider,
  ResourceContextProvider,
  SimpleList,
  TextField,
  Title,
} from "react-admin";
import { ATTESTATIONS, GraduationServicePath, MOBILE_BREAKPOINT, MONTHLY_INTERNSHIP } from "utils/constants";
import { graduationRequests } from "api/graduationApi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import AddIcon from "@mui/icons-material/Add";
import BackButtonToolbar from "features/BackButtonToolbar";
import dataProvider from "server/dataProvider";
import { EmptyList } from "server/components";
import { Box, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

import DeleteAttestationDialog from "./DeleteAttestationDialog";

const ListMonthlyInternshipAttestations = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const startDate = location?.state?.startDate;

  const [attestationsData, setAttestationsData] = useState();

  const fetchAttestations = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(graduationRequests.getStudentMonthlyInternshipAttestations(id));
      setAttestationsData(data);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchAttestations();
  }, [id, fetchAttestations]);

  if (attestationsData === null) return null;

  const lastAttestation = attestationsData?.at(-1) ?? null;

  return (
    <>
      <Title title="Заверки за месечен стаж" />
      <BackButtonToolbar />
      <Box sx={{ display: "flex", justifyContent: "end", mt: 2, mb: 1 }}>
        <SecuredComponent permission={permissions.canManageMonthlyInternshipAttestations}>
          <Button
            startIcon={<AddIcon />}
            size="small"
            label="Добави заверка"
            onClick={() =>
              navigate(`/${GraduationServicePath}${MONTHLY_INTERNSHIP}/students/${ATTESTATIONS}/create`, {
                state: { studentEducationId: id, startDate, lastAttestationDate: lastAttestation?.attestationDate },
              })
            }
          />
        </SecuredComponent>
      </Box>
      {attestationsData?.length === 0 ? (
        <EmptyList />
      ) : (
        <ListContextProvider value={{ data: attestationsData }}>
          <ResourceContextProvider value={attestationsData}>
            {isMobile ? (
              <SimpleList
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <MobileListRow
                    fields={[
                      {
                        label: "Дата на заверка",
                        value: (
                          <DateField
                            source="attestationDate"
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                )}
                secondaryText={record => {
                  const lastId = Math.max(...attestationsData.map(a => a.id));
                  return (
                    <div>
                      <MobileListRow fields={[{ label: "Бележка", value: record?.note }]} />
                      {record.id === lastId && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          <SecuredComponent permission={permissions.canManageMonthlyInternshipAttestations}>
                            <DeleteAttestationDialog
                              fetchAttestations={fetchAttestations}
                              attestationId={record.id}
                              id={id}
                            />
                          </SecuredComponent>
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            ) : (
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={{
                  "& .RaDatagrid-headerCell": {
                    textAlign: "left",
                  },
                }}
              >
                <DateField
                  source="attestationDate"
                  label="Дата на заверка"
                  locales="bg-BG"
                />
                <TextField
                  source="note"
                  label="Бележка"
                />
                <FunctionField
                  render={rec => {
                    const lastId = Math.max(...attestationsData.map(a => a.id));

                    return rec.id === lastId ? (
                      <SecuredComponent permission={permissions.canManageMonthlyInternshipAttestations}>
                        <DeleteAttestationDialog
                          fetchAttestations={fetchAttestations}
                          attestationId={rec.id}
                          id={id}
                        />
                      </SecuredComponent>
                    ) : null;
                  }}
                />
              </Datagrid>
            )}
          </ResourceContextProvider>
        </ListContextProvider>
      )}
    </>
  );
};

export default ListMonthlyInternshipAttestations;
